import { useMemo } from 'react';
import { Flex, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { SlaLabeledSelect } from '~/app/landing/sla/shared/components/SlaLabeledSelect';
import type {
  MaintenanceContractIssue,
  MaintenanceSlaDraftTimer,
  MaintenanceSlaLevelCode,
  MaintenanceSlaTimerKind,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/types';
import {
  MAINTENANCE_CALENDAR_MODE_OPTIONS,
  MAINTENANCE_CLOCK_START_OPTIONS,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/utils';
import { useMaintenanceSlaIssueHelp } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/hooks/useMaintenanceSlaIssueHelp';

type TimerChange = (
  kind: MaintenanceSlaTimerKind,
  patch: Partial<MaintenanceSlaDraftTimer>,
) => void;

type Args = {
  levelCode: MaintenanceSlaLevelCode;
  issues: readonly MaintenanceContractIssue[];
  onTimerChange: TimerChange;
};

export function useMaintenanceSlaTimerColumns({
  levelCode,
  issues,
  onTimerChange,
}: Args) {
  const { t } = useTranslation('sla');
  const help = useMaintenanceSlaIssueHelp(issues);

  return useMemo(
    () => [
      {
        title: t('oems.maintenance.kind'),
        key: 'kind',
        width: 170,
        render: (_: unknown, timer: MaintenanceSlaDraftTimer) => (
          <span style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ color: '#191c1f', fontWeight: 500 }}>
              {t(`oems.maintenance.timerKind.${timer.kind}`)}
            </span>
            <span
              style={{
                color: '#94a3b8',
                fontSize: 11,
                lineHeight: 1.3,
                marginTop: 2,
              }}
            >
              {t(`oems.maintenance.timerKindExplanation.${timer.kind}`)}
            </span>
          </span>
        ),
      },
      {
        title: t('oems.maintenance.clockStartLabel'),
        key: 'clockStart',
        width: 220,
        render: (_: unknown, timer: MaintenanceSlaDraftTimer) => {
          const clockHelp = help('timerClock', {
            levelCode,
            kind: timer.kind,
          });

          return (
            <Flex vertical gap={4}>
              <SlaLabeledSelect
                style={{ width: '100%' }}
                placeholder={t('oems.maintenance.selectPlaceholder')}
                value={timer.clockStart ?? undefined}
                options={MAINTENANCE_CLOCK_START_OPTIONS}
                translationPrefix="oems.maintenance.clockStart"
                onChange={(value) =>
                  onTimerChange(timer.kind, {
                    clockStart: value,
                  })
                }
              />
              {clockHelp ? (
                <Typography.Text type="danger">{clockHelp}</Typography.Text>
              ) : null}
            </Flex>
          );
        },
      },
      {
        title: t('oems.maintenance.calendarModeLabel'),
        key: 'calendarMode',
        width: 260,
        render: (_: unknown, timer: MaintenanceSlaDraftTimer) => {
          const calendarHelp = help('timerCalendar', {
            levelCode,
            kind: timer.kind,
          });

          return (
            <Flex vertical gap={4}>
              <SlaLabeledSelect
                style={{ width: '100%' }}
                placeholder={t('oems.maintenance.selectPlaceholder')}
                value={timer.calendarMode ?? undefined}
                options={MAINTENANCE_CALENDAR_MODE_OPTIONS}
                translationPrefix="oems.maintenance.calendarMode"
                onChange={(value) =>
                  onTimerChange(timer.kind, {
                    calendarMode: value,
                  })
                }
              />
              {calendarHelp ? (
                <Typography.Text type="danger">{calendarHelp}</Typography.Text>
              ) : null}
            </Flex>
          );
        },
      },
    ],
    [help, levelCode, onTimerChange, t],
  );
}
